import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { BACKEND_URL as APP_BACKEND_URL } from '@/lib/backendUrl';

const API = `${APP_BACKEND_URL}/api`;
const storageKey = (audience) => `sahiri_invite_${audience}`;

/**
 * Unlock logic for invite-gated public pages. Unlocks for:
 *   1. Logged-in admins (automatic bypass)
 *   2. Any session that has already verified a code (sessionStorage)
 *   3. Visitors arriving with a valid ?code= query param (auto-verified)
 *   4. Manual code entry via submit()
 * Access lasts for the current browser session only.
 */
export function useInviteGate(audience) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [autoUnlocking, setAutoUnlocking] = useState(false);

  const isAdmin = !!user?.is_admin;

  const verify = useCallback(async (rawCode) => {
    const cleaned = (rawCode || '').trim().toUpperCase();
    if (!cleaned) return { ok: false, message: 'Enter a code' };
    try {
      const res = await axios.post(`${API}/invite-codes/verify`, {
        code: cleaned,
        audience,
      });
      if (res?.data?.valid) {
        try { sessionStorage.setItem(storageKey(audience), cleaned); } catch (_) {}
        return { ok: true };
      }
      return { ok: false, message: 'Invalid code' };
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Invalid code';
      return { ok: false, message: detail };
    }
  }, [audience]);

  // On mount: admin bypass → sessionStorage → ?code= query param → prompt
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isAdmin) {
        if (!cancelled) { setUnlocked(true); setChecking(false); }
        return;
      }
      let stored = null;
      try { stored = sessionStorage.getItem(storageKey(audience)); } catch (_) {}
      if (stored) {
        const r = await verify(stored);
        if (!cancelled) {
          if (r.ok) { setUnlocked(true); setChecking(false); return; }
          try { sessionStorage.removeItem(storageKey(audience)); } catch (_) {}
        }
      }
      const params = new URLSearchParams(location.search);
      const queryCode = params.get('code');
      if (queryCode) {
        if (!cancelled) setAutoUnlocking(true);
        const r = await verify(queryCode);
        if (!cancelled) {
          if (r.ok) {
            // Strip ?code= from the URL so the code doesn't linger in history/share
            params.delete('code');
            const cleanSearch = params.toString();
            navigate({ pathname: location.pathname, search: cleanSearch ? `?${cleanSearch}` : '' }, { replace: true });
            setUnlocked(true);
          } else {
            setError(r.message || 'That access link is no longer valid.');
          }
          setAutoUnlocking(false);
          setChecking(false);
          return;
        }
      }
      if (!cancelled) setChecking(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, audience]);

  const submit = useCallback(async (code) => {
    setError('');
    setSubmitting(true);
    const r = await verify(code);
    setSubmitting(false);
    if (r.ok) setUnlocked(true);
    else setError(r.message || 'Invalid code');
  }, [verify]);

  return { unlocked, checking, autoUnlocking, error, submitting, submit };
}
